import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SocketService from '../../../../../packages/shared/socket';
import API_ENDPOINTS from '../../config/api';
import { isInstanceVisibleInAdminSelector } from '../../utils/statusTranslations';
import { usePublishTopChromeHeight } from '../../hooks/usePublishTopChromeHeight.js';
import { ADMIN_INSTANCE_STORAGE_KEY, readRememberedInstanceId } from '../../hooks/useAdminInstance';
import { AuthContext } from '../AuthGate';
import { hasRole } from '../../utils/roles';
import { grantedInstanceIds } from '../../utils/access';
import InstanceSwitcher from './InstanceSwitcher';
import AdminSettingsMenu from './AdminSettingsMenu';
import InstanceNav from './InstanceNav';
import StageSwitcher from './StageSwitcher';
import NavMenu from './NavMenu';
import useStageNav from './useStageNav';
import {
  INSTANCE_HOME, SERVER_NAV, FACILITATOR_HOME, isNavItemActive, getNavScope, canOpen, stagesFor,
} from './adminNav';
import {
  HeaderShell,
  Header,
  HeaderSpacer,
  LeftSection,
  LogoSection,
  LogoRow,
  LogoIcon,
  LogoText,
  Divider,
  InstanceZone,
  WideOnly,
  ZoneLinks,
  ServerZone,
  ZoneLabel,
  NavLink,
  RightSection,
  MobileNav,
  DrawerHeading,
  DrawerLink,
  NavBadge,
} from './AdminHeader.styles';

export { HEADER_HEIGHT } from './AdminHeader.styles';

const qaBadgeColor = (summary) =>
  summary.red > 0 ? 'red' : summary.yellow > 0 ? 'yellow' : summary.tested > 0 ? 'green' : null;

/**
 * AdminHeader - Global header for admin views.
 *
 * Top bar: the instance zone — the chip and, on a wide screen, the stage
 * selector (StageSwitcher) — and the server zone ("Ylläpito", server-wide
 * pages). On instance pages a second row, InstanceNav, holds the active
 * stage's tabs (and the stage selector below TOP_STAGES_MIN). On a server page
 * the instance zone is dimmed, one click back. Below 1500 px Ylläpito's pages
 * go into a hamburger menu (NavMenu); the stage row has its own for the stage.
 */
const AdminHeader = ({
  instanceId,
  instanceName,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isAdmin = hasRole(user, 'admin');
  // The page's instance, else the one last worked on (server pages carry none).
  const activeInstanceId = instanceId || readRememberedInstanceId();
  // A facilitator sees only what they may open — by role, or by their grant
  // on the selected instance (specs/access-model.md) — and only their instances.
  const stages = stagesFor(user, activeInstanceId);
  const serverNav = SERVER_NAV.filter(item => canOpen(user, item));
  const showHome = canOpen(user, INSTANCE_HOME, activeInstanceId);
  const homePath = showHome ? INSTANCE_HOME.path : FACILITATOR_HOME;
  const scope = getNavScope(location.pathname);

  const [isConnected, setIsConnected] = useState(false);
  const [instances, setInstances] = useState([]);
  const [qaSummary, setQaSummary] = useState(null);
  const socketRef = useRef(null);
  const headerRef = useRef(null);

  usePublishTopChromeHeight(headerRef);

  useEffect(() => {
    fetch(API_ENDPOINTS.INSTANCES())
      .then(r => r.json())
      .then(data => setInstances((Array.isArray(data) ? data : [])
        .filter(isInstanceVisibleInAdminSelector)
        .filter(inst => isAdmin || grantedInstanceIds(user).includes(String(inst.id)))))
      .catch(err => console.error('Failed to fetch instances:', err));
  }, [isAdmin, user]);

  // Fetch QA summary for nav badge
  useEffect(() => {
    if (!isAdmin) return;
    fetch(API_ENDPOINTS.QA_SUMMARY())
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setQaSummary(data); })
      .catch(() => {});
  }, [isAdmin]);

  // Connect to socket for connection status
  useEffect(() => {
    console.log('[AdminHeader] useEffect running, activeInstanceId:', activeInstanceId);

    if (!activeInstanceId) {
      console.log('[AdminHeader] No activeInstanceId, setting disconnected');
      setIsConnected(false);
      return;
    }

    const socket = SocketService.connect(activeInstanceId, 'admin', 'admin-header');
    socketRef.current = socket;

    console.log('[AdminHeader] Socket obtained, connected:', socket.connected);

    // Set initial state
    setIsConnected(socket.connected);

    const handleConnect = () => {
      console.log('[AdminHeader] Socket connect event');
      setIsConnected(true);
    };
    const handleDisconnect = () => {
      console.log('[AdminHeader] Socket disconnect event');
      setIsConnected(false);
    };

    // Poll connection status until connected (handles race condition with other components)
    const checkConnection = setInterval(() => {
      if (socket.connected) {
        console.log('[AdminHeader] Polling detected connection');
        setIsConnected(true);
        clearInterval(checkConnection);
      }
    }, 100);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    return () => {
      clearInterval(checkConnection);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [activeInstanceId]);

  const handleGoHome = () => {
    navigate(activeInstanceId ? `${homePath}?instance=${activeInstanceId}` : homePath);
  };

  // On an instance page, reload the same page for the new instance; from a
  // server page, choosing an instance means going back to work on it.
  const handleSelectInstance = (newInstanceId) => {
    localStorage.setItem(ADMIN_INSTANCE_STORAGE_KEY, newInstanceId);
    const path = scope === 'server' ? homePath : location.pathname;
    navigate(`${path}?instance=${newInstanceId}`);
  };

  const handleRefreshConnection = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current.connect();
    }
  };

  const listedName = instances.find(inst => inst.id === activeInstanceId);
  const chipLabel = (instanceId && instanceName)
    || listedName?.title || listedName?.name
    || (activeInstanceId ? `${activeInstanceId.substring(0, 8)}…` : 'Valitse instanssi');

  const buildInstancePath = (path) => `${path}?instance=${activeInstanceId}`;
  const instancePath = (item) => buildInstancePath(item.path);
  const showStageRow = scope === 'instance' && !!activeInstanceId;
  const stageNav = useStageNav({
    instanceId: activeInstanceId, stages, pathname: location.pathname, onNavigate: navigate,
  });

  const renderNavLabel = (item) => {
    if (item.key === 'qa' && qaSummary) {
      return (
        <NavBadge $color={qaBadgeColor(qaSummary)}>
          {qaSummary.tested}/{qaSummary.total}
        </NavBadge>
      );
    }
    return item.label;
  };

  // One tab, as a desktop NavLink or a mobile DrawerLink.
  const renderLink = (Link, item, { to, label = renderNavLabel(item) }) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.key}
        $active={isNavItemActive(item, location.pathname)}
        onClick={() => navigate(to)}
      >
        <Icon size={18} />
        {label}
      </Link>
    );
  };

  return (
    <>
    <HeaderShell ref={headerRef}>
    <Header>
      <LeftSection>
        <LogoSection onClick={handleGoHome}>
          <LogoRow>
            <LogoIcon>🔧</LogoIcon>
            <LogoText>Kuutio Admin</LogoText>
          </LogoRow>
        </LogoSection>

        <Divider />

        <InstanceZone $dimmed={scope === 'server'}>
          <InstanceSwitcher
            instanceId={activeInstanceId}
            label={chipLabel}
            instances={instances}
            isConnected={isConnected}
            current={scope === 'instance' && !!activeInstanceId}
            onSelect={handleSelectInstance}
          />
          {activeInstanceId && stages.length > 0 && (
            <WideOnly>
              <StageSwitcher
                stages={stages}
                showHome={showHome}
                pathname={location.pathname}
                activeStage={stageNav.activeStage}
                instanceStage={stageNav.instanceStage}
                buildPath={buildInstancePath}
                navTo={stageNav.navTo}
              />
            </WideOnly>
          )}
        </InstanceZone>
      </LeftSection>

      <RightSection>
        {serverNav.length > 0 && (
          <ServerZone>
            <ZoneLabel>Ylläpito</ZoneLabel>
            <ZoneLinks aria-label="Ylläpito">
              {serverNav.map(item => renderLink(NavLink, item, { to: item.path }))}
            </ZoneLinks>
          </ServerZone>
        )}

        {serverNav.length > 0 && (
          <MobileNav>
            <NavMenu label="Ylläpito" align="right">
              <DrawerHeading>Ylläpito</DrawerHeading>
              {serverNav.map(item => renderLink(DrawerLink, item, { to: item.path, label: item.label }))}
            </NavMenu>
          </MobileNav>
        )}

        <AdminSettingsMenu
          instanceId={activeInstanceId}
          onGoHome={handleGoHome}
          onRefreshConnection={handleRefreshConnection}
        />
      </RightSection>
    </Header>
    {showStageRow && (
      <InstanceNav
        instanceId={activeInstanceId}
        stages={stages}
        showHome={showHome}
        pathname={location.pathname}
        buildPath={buildInstancePath}
        stageNav={stageNav}
      />
    )}
    </HeaderShell>
    <HeaderSpacer aria-hidden="true" $withStageRow={showStageRow} />
    </>
  );
};

export default AdminHeader;
